import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PublicNav, PublicFooter } from "./PublicHome";
import { TYPE_LABEL, useWorkforceCta, SolutionCard } from "../components/WorkforceCta";
import InFeedAd, { isAdBreak, useAdRegistry } from "../components/InFeedAd";
import { Bot, Users, Building2, Sparkles, ArrowRight } from "lucide-react";

const TYPE_ICON = {
  HUMAN_DIRECT_HIRE: Users, MANAGED_HUMAN_TEAM: Users, AI_EMPLOYEE: Bot, AI_DEPARTMENT: Bot,
  AI_EXECUTIVE: Sparkles, AI_SALES_WORKFORCE: Bot, HYBRID_WORKFORCE: Building2, SALESHUB_PLAN: Bot, FRONTDESK_MEMBERSHIP: Bot,
};

export default function Workforce() {
  const [state, setState] = useState({ solutions: [], categories: [], departments: [], solution_types: [] });
  const [filters, setFilters] = useState({ solution_type: "", department: "", q: "" });
  const { handleCta, modal, checkoutMsg } = useWorkforceCta();
  const adReg = useAdRegistry();

  useEffect(() => {
    const params = {};
    for (const k of ["solution_type", "department", "q"]) if (filters[k]) params[k] = filters[k];
    api.get("/public/workforce", { params }).then(r => setState(r.data)).catch(() => {});
  }, [filters]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of state.solutions) (g[s.solution_type] = g[s.solution_type] || []).push(s);
    return g;
  }, [state.solutions]);
  const deptCounts = useMemo(() => {
    const c = {};
    for (const s of state.solutions) if (s.department) c[s.department] = (c[s.department] || 0) + 1;
    return c;
  }, [state.solutions]);
  const sel = "h-9 rounded-md border border-slate-300 px-3 text-sm";

  return (
    <div className="min-h-screen bg-white">
      <PublicNav/>
      <main>
        <section className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-300 mb-3">AI Workforce Marketplace™ · ConnectMyHub AI Employees</div>
            <h1 className="font-display font-bold text-3xl md:text-5xl leading-tight tracking-tight max-w-3xl" data-testid="workforce-headline">BUILD YOUR WORKFORCE.</h1>
            <p className="text-slate-300 text-base md:text-lg mt-5 max-w-2xl">Hire people. Hire AI Employees. Add an AI Department. Add a managed team. Build a hybrid workforce.</p>
            <p className="text-slate-400 text-sm mt-3 max-w-2xl">People. Skills. Business. Connected.™ AI workforce products are managed AI services, not human employees.</p>
            <button onClick={() => document.getElementById("wf-departments")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-build-cta"
                    className="mt-8 h-11 px-6 rounded-md bg-teal-500 text-slate-950 text-xs font-semibold uppercase tracking-widest hover:bg-teal-400 inline-flex items-center gap-2">Build My AI Workforce <ArrowRight size={12}/></button>
          </div>
        </section>

        <section id="wf-departments" className="max-w-6xl mx-auto px-6 py-10 border-b border-slate-100">
          <h2 className="font-display font-bold text-base md:text-lg text-slate-900">Browse AI Departments</h2>
          <p className="text-sm text-slate-500 mt-1">Every AI Employee reports into a department. Pick the function you need covered.</p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="department-grid">
            {state.departments.map(d => (
              <Link key={d.slug} to={`/workforce/departments/${d.slug}`} data-testid={`dept-card-${d.slug}`}
                    className="rounded-lg border border-slate-200 p-4 hover:border-teal-400 hover:shadow-sm transition-colors">
                <div className="font-semibold text-slate-900 text-sm">{d.name}</div>
                <div className="text-xs text-slate-500 mt-1 line-clamp-2">{d.purpose}</div>
                <div className="text-[11px] text-teal-700 mt-2 font-semibold">{deptCounts[d.name] || 0} workforce solution{deptCounts[d.name] === 1 ? "" : "s"} →</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-6 border-b border-slate-100">
          <div className="flex flex-wrap gap-2 items-center">
            <select value={filters.solution_type} onChange={e => setFilters({ ...filters, solution_type: e.target.value })} data-testid="filter-type" className={sel}>
              <option value="">All workforce types</option>
              {state.solution_types.map(t => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
            </select>
            <select value={filters.department} onChange={e => setFilters({ ...filters, department: e.target.value })} data-testid="filter-department" className={sel}>
              <option value="">All departments</option>
              {state.departments.map(d => <option key={d.slug} value={d.name}>{d.name}</option>)}
            </select>
            <input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} data-testid="filter-search"
                   placeholder="Search AI workforce e.g. CFO, receivable, support…" className={`${sel} flex-1 min-w-[220px]`}/>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-10 space-y-10">
          {state.solutions.length === 0 && <p className="text-sm text-slate-500" data-testid="wf-empty">No workforce solutions match your filters.</p>}
          {(() => { let running = 0; const total = state.solutions.length; return state.solution_types.filter(t => grouped[t]?.length).map(t => {
            const Icon = TYPE_ICON[t] || Bot;
            return (
              <div key={t} data-testid={`wf-group-${t}`}>
                <h2 className="font-display font-bold text-xl text-slate-900 mb-3 flex items-center gap-2"><Icon size={18}/>{TYPE_LABEL[t] || t}</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {grouped[t].map(s => { const gi = running++; return (
                    <Fragment key={s.sku}>
                      <SolutionCard s={s} onCta={handleCta} checkoutMsg={checkoutMsg}/>
                      {isAdBreak(gi, total) && <div className="md:col-span-2 lg:col-span-3"><InFeedAd zone="WORKFORCE_MARKETPLACE_INFEED" registry={adReg}/></div>}
                    </Fragment>
                  ); })}
                </div>
              </div>
            );
          }); })()}
        </section>
        {modal}
      </main>
      <PublicFooter/>
    </div>
  );
}
